<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  element:       { type: Object,  default: () => ({}) },
  estimation:    { type: Object,  default: () => ({}) },
  partie_suivie: { type: Object,  default: () => ({}) },
  can_see_costs: { type: Boolean, default: false },
  back_url:      { type: String,  default: '#' },
  errors:        { type: Object,  default: () => ({}) },
})

const form = useForm({
  estimation_partie_suivie_id: props.element.estimation_partie_suivie_id || props.partie_suivie.id || '',
  code:          props.element.code          || '',
  description:   props.element.description   || '',
  quantite:      props.element.quantite      || '',
  unite:         props.element.unite         || '',
  taux_unitaire: props.element.taux_unitaire  || '',
  actif:         props.element.actif !== undefined ? props.element.actif : true,
})

const { error: toastError } = useToast()

const fieldLabels = {
  code:          'Code',
  description:   'Description',
  quantite:      'Quantité',
  unite:         'Unité',
  taux_unitaire: 'Taux unitaire',
  actif:         'Actif',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}

function submit() {
  form.transform(data => ({ estimation_partie_suivie_element: data })).post('/estimation_partie_suivie_elements', {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

function cancel() {
  window.location.href = props.back_url
}

const fieldError = (field) =>
  form.errors[field] || props.errors[field]?.[0] || null
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        {{ partie_suivie.code }} &rsaquo; Nouvel élément
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Nouvel élément — {{ partie_suivie.code }}
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              Estimation <a :href="estimation.show_url" class="text-primary hover:brightness-125 transition-all">{{ estimation.code }}</a>
            </p>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Form Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Code + Description -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Code <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  placeholder="ex. ELT-001"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('code') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('code')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('code') }}</p>
              </div>

              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Description <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.description"
                  type="text"
                  placeholder="Description de l'élément"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('description') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('description')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('description') }}</p>
              </div>
            </div>

            <!-- Quantité + Unité + Taux unitaire -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Quantité <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.quantite"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('quantite') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('quantite')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('quantite') }}</p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Unité
                </label>
                <input
                  v-model="form.unite"
                  type="text"
                  placeholder="ex. m³, kg, unité"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('unite') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('unite')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('unite') }}</p>
              </div>

              <div v-if="can_see_costs">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Taux unitaire ($)
                </label>
                <input
                  v-model="form.taux_unitaire"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('taux_unitaire') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('taux_unitaire')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('taux_unitaire') }}</p>
              </div>
            </div>

            <!-- Actif -->
            <div>
              <label class="flex items-center gap-3 cursor-pointer select-none">
                <input
                  v-model="form.actif"
                  type="checkbox"
                  class="w-4 h-4 accent-primary cursor-pointer"
                />
                <span class="text-[10px] font-black text-outline uppercase tracking-widest">Actif</span>
              </label>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                @click="cancel"
                class="px-6 py-3 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="form.processing"
                class="px-8 py-3 machined-sheen text-on-primary font-black rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">add_circle</span>
                Créer l'élément
              </button>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
